import {
  BenchlingExpression,
  BenchlingExpressionConstantValue,
  BenchlingExpressionFunction,
  BenchlingExpressionFunctionParameter,
  BenchlingExpressionType,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import deepCopy from 'lib/deepCopy';

export interface NotificationSchedule {
  recipientExpr: BenchlingExpression;
  deliveryDateExpr: BenchlingExpression;
  evaluatorVersion: EvaluatorVersion;
}

export enum RecipientType {
  ASSIGNEE = 'assignee',
  WATCHERS = 'watchers',
}

export const DEFAULT_RECIPIENT = RecipientType.ASSIGNEE;
export const DEFAULT_DAYS = 1;
export const DEFAULT_SUBTRACT_FROM_DATETIME = true;

export enum EvaluatorVersion {
  V1 = 'v1',
}

const DEFAULT_RECIPIENT_EXPR: BenchlingExpressionConstantValue = {
  itemType: {
    name: LeafType.TEXT,
    selectorId: null,
  },
  type: BenchlingExpressionType.CONSTANT_VALUE,
  value: DEFAULT_RECIPIENT,
};

const DEFAULT_SET_DATE_ON_DATETIME_EXPR: BenchlingExpressionFunction = {
  name: 'SET_TIME_ON_DATETIME_V1',
  parameters: [
    {
      expression: {
        contextKey: 'scheduled_on_date',
        itemType: {name: LeafType.DATE},
        type: BenchlingExpressionType.CONTEXT_VARIABLE,
      },
      name: 'datetime',
    },
    {
      expression: {
        contextKey: 'timezone',
        itemType: {name: LeafType.TEXT},
        type: BenchlingExpressionType.CONTEXT_VARIABLE,
      },
      name: 'timezone',
    },
    {
      expression: {
        itemType: {name: LeafType.INTEGER},
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: 0,
      },
      name: 'second',
    },
    {
      expression: {
        itemType: {name: LeafType.INTEGER},
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: 0,
      },
      name: 'minute',
    },
    {
      expression: {
        itemType: {name: LeafType.INTEGER},
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: 0,
      },
      name: 'hour',
    },
  ],
  type: BenchlingExpressionType.FUNCTION,
};

const DEFAULT_DELIVERY_DATE_EXPR: BenchlingExpressionFunction = {
  name: 'CALCULATE_DATETIME_V1',
  parameters: [
    {
      expression: DEFAULT_SET_DATE_ON_DATETIME_EXPR,
      name: 'datetime',
    },
    {
      expression: {
        itemType: {
          name: LeafType.BOOLEAN,
          selectorId: null,
        },
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: DEFAULT_SUBTRACT_FROM_DATETIME,
      },
      name: 'subtract_from_datetime',
    },
    {
      expression: {
        itemType: {name: LeafType.INTEGER},
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: 0,
      },
      name: 'seconds',
    },
    {
      expression: {
        itemType: {name: LeafType.INTEGER},
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: 0,
      },
      name: 'minutes',
    },
    {
      expression: {
        itemType: {name: LeafType.INTEGER},
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: 0,
      },
      name: 'hours',
    },
    {
      expression: {
        itemType: {
          name: LeafType.INTEGER,
          selectorId: null,
        },
        type: BenchlingExpressionType.CONSTANT_VALUE,
        value: DEFAULT_DAYS,
      },
      name: 'days',
    },
  ],
  type: BenchlingExpressionType.FUNCTION,
};

export function _getParamFromName(
  expr: BenchlingExpressionFunction,
  name: string
): BenchlingExpressionFunctionParameter | undefined {
  return expr.parameters.find((param) => param.name === name);
}

function setParamValueFromName(expr: BenchlingExpressionFunction, param: string, value: unknown): void {
  (_getParamFromName(expr, param)?.expression as BenchlingExpressionConstantValue).value = value;
}

function setExpressionFromName(
  expr: BenchlingExpressionFunction,
  name: string,
  expression: BenchlingExpression
): void {
  (_getParamFromName(expr, name) as BenchlingExpressionFunctionParameter).expression = expression;
}

export function createNewRecipientExpr(recipient: string): BenchlingExpressionConstantValue {
  const newRecipientExpr = deepCopy(DEFAULT_RECIPIENT_EXPR);
  newRecipientExpr.value = recipient;
  return newRecipientExpr;
}

export function createNewSetTimeOnDatetimeExpr(
  second: number,
  minute: number,
  hour: number
): BenchlingExpressionFunction {
  const newSettimeOnDatetimeExpr = deepCopy(DEFAULT_SET_DATE_ON_DATETIME_EXPR);
  setParamValueFromName(newSettimeOnDatetimeExpr, 'second', second);
  setParamValueFromName(newSettimeOnDatetimeExpr, 'minute', minute);
  setParamValueFromName(newSettimeOnDatetimeExpr, 'hour', hour);
  return newSettimeOnDatetimeExpr;
}

export function createNewDeliveryDateExpr(
  days: number,
  subtractFromDatetime: boolean,
  setTimeOnDatetimeExpr: BenchlingExpressionFunction
): BenchlingExpressionFunction {
  const newDeliveryDateExpr = deepCopy(DEFAULT_DELIVERY_DATE_EXPR);
  setParamValueFromName(newDeliveryDateExpr, 'days', days);
  setParamValueFromName(newDeliveryDateExpr, 'subtract_from_datetime', subtractFromDatetime);
  setExpressionFromName(newDeliveryDateExpr, 'datetime', setTimeOnDatetimeExpr);
  return newDeliveryDateExpr;
}

export function getRecipientFromExprV1(recipientExpr: BenchlingExpression): RecipientType {
  return (recipientExpr as BenchlingExpressionConstantValue).value as RecipientType;
}

export function getDaysFromExprV1(deliveryDateExpr: BenchlingExpression): number {
  const daysExpr = _getParamFromName(deliveryDateExpr as BenchlingExpressionFunction, 'days');
  return (daysExpr?.expression as BenchlingExpressionConstantValue).value as number;
}

export function getSubtractFromDatetimeFromExprV1(deliveryDateExpr: BenchlingExpression): boolean {
  const subtractFromDatetimeExpr = _getParamFromName(
    deliveryDateExpr as BenchlingExpressionFunction,
    'subtract_from_datetime'
  );
  return (subtractFromDatetimeExpr?.expression as BenchlingExpressionConstantValue).value as boolean;
}

// Keep in sync with expected schedule_evaluators.py version
export function currentEvaluatorVersion(): EvaluatorVersion {
  return EvaluatorVersion.V1;
}
